import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Stack from "@mui/material/Stack";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Badge from "@mui/material/Badge";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { api, get } from "lib/api";
import { firstListMediaUrl, mediaUrl } from "lib/media";
import { useAuth } from "context/AuthContext";
import { useCart } from "context/CartContext";
import { useMobileLocale } from "mobile/i18n";
import { currencyForCompany, normalizeCompanyKey } from "lib/companyProfile";
import { isStaffRole, navigateAccountPortal } from "lib/staffFrontend";

import logo from "assets/images/logos/ShortLogo.png";
import fallbackOne from "assets/images/home-decor-1.jpg";
import fallbackTwo from "assets/images/home-decor-2.jpg";
import fallbackThree from "assets/images/home-decor-3.jpg";

const COLORS = {
  ink: "#0b1118",
  text: "#17202a",
  muted: "#667085",
  line: "rgba(11,17,24,0.1)",
  soft: "#f6f7f9",
  red: "#cf241f",
  green: "#0f766e",
};

const iosUrl = String(process.env.REACT_APP_IOS_APP_URL || "").trim();
const androidUrl = String(process.env.REACT_APP_ANDROID_APP_URL || "").trim();

const heroMessages = [
  ["Precision tested", "Ready for installation."],
  ["Built clean", "Verified before it ships."],
  ["Shop ready", "Catalog details made simple."],
  ["Install with confidence", "Support after the sale."],
  ["Quality checked", "Fitment details up front."],
  ["Remanufactured right", "Tested for reliable flow."],
  ["Fast lookup", "Find the right valve body."],
  ["Proven process", "Clean parts. Clear records."],
  ["Core tracked", "Returns and warranty in one place."],
  ["Built for shops", "Order, install, and track."],
];

function normalizeProduct(item) {
  const attrs = item?.attributes || item || {};
  return {
    id: item?.id || attrs?.documentId || attrs?.sku,
    documentId: item?.documentId || attrs?.documentId,
    sku: attrs.sku || "Pending",
    name: attrs.name || "Valve body",
    price: attrs.unitPrice,
    status: attrs.status,
    imageUrl: firstListMediaUrl(attrs.images),
  };
}

function priceText(value, currency = "USD") {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "Sign in for pricing";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}

function MobileHeader() {
  const navigate = useNavigate();
  const { token, pluginRole } = useAuth();
  const { itemCount } = useCart();
  const { t } = useMobileLocale();
  const iconButtonSx = {
    minWidth: 44,
    width: 44,
    height: 44,
    p: 0,
    borderRadius: 1,
    color: COLORS.ink,
    bgcolor: "#fff",
    border: `1px solid ${COLORS.line}`,
    boxShadow: "0 8px 22px rgba(11,17,24,0.06)",
    "&:hover": {
      bgcolor: "#fff",
      borderColor: "rgba(207,36,31,0.35)",
      color: COLORS.red,
    },
  };
  return (
    <MDBox
      component="header"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        bgcolor: "rgba(255,255,255,0.9)",
        borderBottom: `1px solid ${COLORS.line}`,
        backdropFilter: "blur(18px)",
      }}
    >
      <Container maxWidth="sm" sx={{ py: 1 }}>
        <MDBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1.25}
        >
          <MDBox display="flex" alignItems="center" sx={{ minWidth: 0 }}>
            <MDBox
              component="img"
              src={logo}
              alt="vbspecialties.com"
              sx={{
                width: 78,
                height: "auto",
                objectFit: "contain",
                display: "block",
                flexShrink: 0,
              }}
              onClick={() => navigate("/m")}
            />
          </MDBox>
          <Stack direction="row" spacing={0.75}>
            <MDButton
              variant="outlined"
              sx={iconButtonSx}
              onClick={() => navigate("/m/catalog")}
              aria-label={t("Catalog")}
            >
              <Icon sx={{ fontSize: 24 }}>storefront</Icon>
            </MDButton>
            {token ? (
              <MDButton
                variant="outlined"
                sx={iconButtonSx}
                onClick={() => navigate("/m/cart")}
                aria-label={t("Cart")}
              >
                <Badge
                  badgeContent={itemCount || 0}
                  color="error"
                  invisible={!itemCount}
                >
                  <Icon sx={{ fontSize: 24 }}>shopping_cart</Icon>
                </Badge>
              </MDButton>
            ) : null}
            <MDButton
              variant="contained"
              sx={{
                ...iconButtonSx,
                bgcolor: COLORS.ink,
                color: "#fff",
                borderColor: COLORS.ink,
                "&:hover": { bgcolor: COLORS.red, color: "#fff" },
              }}
              onClick={() =>
                navigateAccountPortal({ token, role: pluginRole, navigate })
              }
              aria-label={
                token && isStaffRole(pluginRole)
                  ? t("Open Staff ERP")
                  : token
                    ? t("Account")
                    : t("Sign in")
              }
            >
              <Icon sx={{ fontSize: 24 }}>{token ? "person" : "login"}</Icon>
            </MDButton>
          </Stack>
        </MDBox>
      </Container>
    </MDBox>
  );
}

function AppDownloadButtons() {
  const navigate = useNavigate();
  const buttonSx = {
    justifyContent: "flex-start",
    borderRadius: 1,
    border: `1px solid ${COLORS.line}`,
    bgcolor: "#fff",
    color: COLORS.ink,
    py: 1.25,
    px: 1,
    fontWeight: 900,
    boxShadow: "none",
    "&:hover": { borderColor: "rgba(207,36,31,0.35)", bgcolor: "#fff" },
  };

  const openDownload = (url) => {
    if (url) {
      window.location.href = url;
      return;
    }
    navigate("/m/qr");
  };

  return (
    <Grid container spacing={0.75}>
      <Grid item xs={6}>
        <MDButton
          fullWidth
          variant="outlined"
          sx={buttonSx}
          onClick={() => openDownload(iosUrl)}
        >
          <Icon sx={{ mr: 1 }}>phone_iphone</Icon>
          iOS
        </MDButton>
      </Grid>
      <Grid item xs={6}>
        <MDButton
          fullWidth
          variant="outlined"
          sx={buttonSx}
          onClick={() => openDownload(androidUrl)}
        >
          <Icon sx={{ mr: 1 }}>android</Icon>
          Android
        </MDButton>
      </Grid>
    </Grid>
  );
}

export default function MobileInfoSite() {
  const navigate = useNavigate();
  const { token, pluginRole, hasPermission, activeCompany, storefrontTenant } = useAuth();
  const canManageHero =
    !!token &&
    typeof hasPermission === "function" &&
    hasPermission("image-gallery");
  const companyKey =
    normalizeCompanyKey(storefrontTenant || activeCompany) || "";
  const currency = currencyForCompany(companyKey);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [gallerySearch, setGallerySearch] = useState("");
  const [selectedHeroIds, setSelectedHeroIds] = useState([]);
  const { data, isLoading } = useQuery({
    queryKey: ["mobile-info-products", companyKey, token ? "auth" : "public"],
    queryFn: () =>
      get("/api/storefront/catalog", {
        sort: "updatedAt:desc",
        pagination: { pageSize: 5 },
      }),
    staleTime: 30000,
  });

  const heroQuery = useQuery({
    queryKey: ["mobile-home-hero-images"],
    queryFn: () => get("/api/media-library/home-hero"),
    staleTime: 30000,
  });

  const galleryQuery = useQuery({
    queryKey: ["mobile-home-gallery-images", gallerySearch],
    queryFn: async () => {
      const res = await api.get("/api/media-library/files", {
        params: { page: 1, pageSize: 60, q: gallerySearch, type: "images" },
      });
      return Array.isArray(res.data)
        ? { data: res.data }
        : res.data || { data: [] };
    },
    enabled: selectorOpen && canManageHero,
    staleTime: 10000,
  });

  const products = useMemo(
    () => (data?.data || []).map(normalizeProduct),
    [data]
  );
  const heroImages = useMemo(
    () =>
      (heroQuery.data?.data || [])
        .map((file) => ({
          id: file.id,
          title: "Valve body catalog",
          hideHeroTitle: true,
          imageUrl: mediaUrl(file),
        }))
        .filter((item) => !!item.imageUrl),
    [heroQuery.data]
  );
  const galleryImages = useMemo(
    () =>
      (galleryQuery.data?.data || [])
        .map((file) => ({
          id: file.id,
          title: file.name || `Image ${file.id}`,
          imageUrl: mediaUrl(file),
        }))
        .filter((item) => !!item.imageUrl),
    [galleryQuery.data]
  );
  const fallbackSlides = useMemo(
    () => [
      { id: "shop", title: "Built for shops", imageUrl: fallbackOne },
      { id: "catalog", title: "Ready to order", imageUrl: fallbackTwo },
      { id: "service", title: "Installer support", imageUrl: fallbackThree },
    ],
    []
  );
  const slides = useMemo(() => {
    if (heroImages.length) return heroImages;
    const productSlides = products
      .filter((product) => product.imageUrl)
      .slice(0, 4)
      .map((product) => ({
        id: product.documentId,
        title: product.name,
        subtitle: product.sku ? `SKU ${product.sku}` : "Catalog item",
        imageUrl: product.imageUrl,
        product,
      }));
    return productSlides.length ? productSlides : fallbackSlides;
  }, [fallbackSlides, heroImages, products]);
  const [activeSlide, setActiveSlide] = useState(0);
  const currentSlide = slides[activeSlide] || slides[0];
  const heroMessage =
    heroMessages[activeSlide % heroMessages.length] || heroMessages[0];

  useEffect(() => {
    if (slides.length <= 1) return undefined;
    const timer = setInterval(() => {
      setActiveSlide((index) => (index + 1) % slides.length);
    }, 4200);
    return () => clearInterval(timer);
  }, [slides.length]);

  useEffect(() => {
    setActiveSlide(0);
  }, [slides.length]);

  useEffect(() => {
    if (!selectorOpen) return;
    setSelectedHeroIds(
      (heroQuery.data?.data || [])
        .map((file) => Number(file.id))
        .filter(Boolean)
    );
  }, [heroQuery.data, selectorOpen]);

  const toggleHeroImage = (id) => {
    const normalized = Number(id);
    if (!Number.isFinite(normalized)) return;
    setSelectedHeroIds((prev) => {
      if (prev.includes(normalized))
        return prev.filter((value) => value !== normalized);
      return [...prev, normalized].slice(0, 6);
    });
  };

  const saveHeroImages = async () => {
    await api.post("/api/media-library/home-hero", {
      imageIds: selectedHeroIds,
    });
    await heroQuery.refetch();
    setSelectorOpen(false);
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#fbfbfa", color: COLORS.text }}>
      <MobileHeader />
      <Container maxWidth="sm" sx={{ px: 2, pt: 2.25, pb: 7 }}>
        <MDBox component="main">
          <MDBox
            sx={{
              position: "relative",
              aspectRatio: "1.05 / 1",
              borderRadius: 1,
              overflow: "hidden",
              bgcolor: COLORS.ink,
              mb: 2.5,
              boxShadow: "0 18px 45px rgba(11,17,24,0.16)",
            }}
          >
            <Box
              component="img"
              src={currentSlide?.imageUrl}
              alt="Valve Body Specialties"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
            <MDBox
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0.08) 20%, rgba(0,0,0,0.74) 100%)",
              }}
            />
            <MDBox
              sx={{ position: "absolute", left: 16, right: 16, bottom: 16 }}
            >
              <MDTypography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.82)", fontWeight: 900 }}
              >
                {heroMessage[0].toUpperCase()}
              </MDTypography>
              <MDTypography
                variant="h3"
                sx={{
                  color: "#fff",
                  fontWeight: 900,
                  lineHeight: 1.05,
                  letterSpacing: 0,
                  mt: 0.35,
                  maxWidth: 300,
                  fontSize: "1.85rem",
                }}
              >
                {heroMessage[1]}
              </MDTypography>
            </MDBox>
          </MDBox>

          <Stack
            direction="row"
            spacing={0.75}
            justifyContent="center"
            sx={{ mb: 2.5 }}
          >
            {slides.map((slide, index) => (
              <Box
                key={slide.id}
                component="button"
                type="button"
                aria-label={`Show slide ${index + 1}`}
                onClick={() => setActiveSlide(index)}
                sx={{
                  width: index === activeSlide ? 22 : 7,
                  height: 7,
                  border: 0,
                  borderRadius: 999,
                  bgcolor:
                    index === activeSlide ? COLORS.red : "rgba(11,17,24,0.22)",
                  p: 0,
                  transition: "width 180ms ease",
                }}
              />
            ))}
          </Stack>

          {canManageHero ? (
            <MDButton
              fullWidth
              variant="outlined"
              sx={{
                borderColor: COLORS.line,
                color: COLORS.ink,
                borderRadius: 1,
                py: 1,
                fontWeight: 900,
                mb: 2.5,
                bgcolor: "#fff",
              }}
              onClick={() => setSelectorOpen(true)}
            >
              Select front page images
            </MDButton>
          ) : null}

          <MDTypography
            variant="h1"
            sx={{
              color: COLORS.ink,
              fontWeight: 900,
              fontSize: "clamp(1.9rem, 7vw, 2.45rem)",
              lineHeight: 1.04,
              letterSpacing: 0,
              mb: 1.25,
            }}
          >
            Premium remanufactured valve bodies.
          </MDTypography>
          <MDTypography
            variant="body1"
            sx={{
              color: COLORS.muted,
              lineHeight: 1.6,
              fontSize: "0.95rem",
              mb: 2.25,
            }}
          >
            Search inventory, review product details, and access seller or
            installer tools.
          </MDTypography>

          <Stack direction="row" spacing={0.75} sx={{ mb: 3.5 }}>
            <MDButton
              fullWidth
              variant="contained"
              sx={{
                bgcolor: COLORS.red,
                color: "#fff",
                borderRadius: 1,
                py: 1.35,
                boxShadow: "none",
                fontWeight: 900,
                "&:hover": { bgcolor: "#af1f1a", boxShadow: "none" },
              }}
              onClick={() => navigate("/m/catalog")}
            >
              View catalog
            </MDButton>
            <MDButton
              fullWidth
              variant="outlined"
              sx={{
                borderColor: COLORS.line,
                color: COLORS.ink,
                borderRadius: 1,
                py: 1.35,
                fontWeight: 900,
              }}
              onClick={() =>
                navigateAccountPortal({ token, role: pluginRole, navigate })
              }
            >
              {token && isStaffRole(pluginRole)
                ? "Open Staff ERP"
                : token
                  ? "Account"
                  : "Account access"}
            </MDButton>
          </Stack>

          <MDBox
            sx={{
              bgcolor: "#fff",
              border: `1px solid ${COLORS.line}`,
              borderRadius: 1,
              p: 2,
              mb: 3,
            }}
          >
            <MDTypography
              variant="h6"
              sx={{ color: COLORS.ink, fontWeight: 900, mb: 1 }}
            >
              Download the app
            </MDTypography>
            <MDTypography
              variant="body2"
              sx={{ color: COLORS.muted, lineHeight: 1.65, mb: 2 }}
            >
              Use the mobile app for account access, catalog review, and order
              activity.
            </MDTypography>
            <AppDownloadButtons />
          </MDBox>

          <Grid container spacing={0.75} sx={{ mb: 3.5 }}>
            {[
              [
                "Sellers",
                "Distributor access for pricing, inventory, and customer orders.",
              ],
              [
                "Installers",
                "Account access for unit registration and service records.",
              ],
              [
                "Catalog",
                "Current product details organized for quick lookup.",
              ],
            ].map(([title, copy]) => (
              <Grid item xs={12} key={title}>
                <MDBox
                  sx={{
                    borderTop: `2px solid ${
                      title === "Sellers" ? COLORS.red : COLORS.green
                    }`,
                    pt: 1.5,
                  }}
                >
                  <MDTypography
                    variant="h6"
                    sx={{ color: COLORS.ink, fontWeight: 900, mb: 0.5 }}
                  >
                    {title}
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    sx={{ color: COLORS.muted, lineHeight: 1.6 }}
                  >
                    {copy}
                  </MDTypography>
                </MDBox>
              </Grid>
            ))}
          </Grid>

          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            mb={1.5}
          >
            <MDTypography
              variant="h5"
              sx={{ color: COLORS.ink, fontWeight: 900 }}
            >
              Featured products
            </MDTypography>
            <MDButton
              variant="text"
              sx={{ color: COLORS.red, fontWeight: 900 }}
              onClick={() => navigate("/m/catalog")}
            >
              View all
            </MDButton>
          </MDBox>

          <Stack spacing={0.75}>
            {isLoading ? (
              <MDBox sx={{ py: 4, textAlign: "center", color: COLORS.muted }}>
                Loading catalog...
              </MDBox>
            ) : products.length ? (
              products.map((product) => (
                <ButtonBase
                  key={product.id}
                  onClick={() => {
                    if (product.documentId) navigate(`/m/catalog/${product.documentId}`);
                  }}
                  sx={{
                    width: "100%",
                    display: "block",
                    textAlign: "left",
                    border: `1px solid ${COLORS.line}`,
                    borderRadius: 1,
                    bgcolor: "#fff",
                    p: 1,
                  }}
                >
                  <MDBox display="flex" alignItems="center" gap={1.25}>
                    <MDBox
                      sx={{
                        width: 70,
                        height: 62,
                        borderRadius: 1,
                        bgcolor: COLORS.soft,
                        overflow: "hidden",
                        flexShrink: 0,
                      }}
                    >
                      {product.imageUrl ? (
                        <Box
                          component="img"
                          src={product.imageUrl}
                          alt={product.name}
                          loading="lazy"
                          decoding="async"
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      ) : (
                        <MDBox
                          height="100%"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Icon sx={{ color: "#98a2b3" }}>
                            precision_manufacturing
                          </Icon>
                        </MDBox>
                      )}
                    </MDBox>
                    <MDBox sx={{ minWidth: 0 }}>
                      <MDTypography
                        variant="button"
                        sx={{
                          display: "block",
                          color: COLORS.ink,
                          fontWeight: 900,
                        }}
                      >
                        {product.name}
                      </MDTypography>
                      <MDTypography
                        variant="caption"
                        sx={{ color: COLORS.muted }}
                      >
                        SKU {product.sku}
                      </MDTypography>
                      <MDTypography
                        variant="button"
                        sx={{
                          color:
                            Number(product.price) > 0 ? COLORS.red : COLORS.ink,
                          fontWeight: 900,
                          display: "block",
                          mt: 0.25,
                        }}
                      >
                        {priceText(product.price, currency)}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </ButtonBase>
              ))
            ) : (
              <MDBox sx={{ py: 4, textAlign: "center", color: COLORS.muted }}>
                Catalog items will appear here.
              </MDBox>
            )}
          </Stack>
        </MDBox>
      </Container>

      <Dialog
        open={selectorOpen}
        onClose={() => setSelectorOpen(false)}
        fullScreen
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Front page images</DialogTitle>
        <DialogContent dividers sx={{ bgcolor: "#fbfbfa" }}>
          <TextField
            fullWidth
            value={gallerySearch}
            onChange={(event) => setGallerySearch(event.target.value)}
            placeholder="Search gallery images"
            sx={{ mb: 2, bgcolor: "#fff" }}
          />
          <Grid container spacing={0.75}>
            {galleryImages.map((image) => {
              const selected = selectedHeroIds.includes(Number(image.id));
              return (
                <Grid item xs={6} key={image.id}>
                  <ButtonBase
                    onClick={() => toggleHeroImage(image.id)}
                    sx={{
                      width: "100%",
                      display: "block",
                      textAlign: "left",
                      border: selected
                        ? `2px solid ${COLORS.red}`
                        : `1px solid ${COLORS.line}`,
                      borderRadius: 1,
                      overflow: "hidden",
                      bgcolor: "#fff",
                    }}
                  >
                    <Box
                      component="img"
                      src={image.imageUrl}
                      alt={image.title}
                      sx={{
                        width: "100%",
                        aspectRatio: "1 / 1",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                    <MDTypography
                      variant="caption"
                      sx={{
                        display: "block",
                        p: 1,
                        color: COLORS.ink,
                        fontWeight: 800,
                      }}
                      noWrap
                    >
                      {image.title}
                    </MDTypography>
                  </ButtonBase>
                </Grid>
              );
            })}
          </Grid>
          {!galleryImages.length ? (
            <MDBox sx={{ py: 5, textAlign: "center", color: COLORS.muted }}>
              {galleryQuery.isLoading
                ? "Loading images..."
                : "No gallery images found."}
            </MDBox>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <MDButton
            variant="text"
            sx={{ color: COLORS.ink, fontWeight: 900 }}
            onClick={() => setSelectorOpen(false)}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="contained"
            sx={{
              bgcolor: COLORS.red,
              color: "#fff",
              borderRadius: 1,
              fontWeight: 900,
              boxShadow: "none",
            }}
            onClick={saveHeroImages}
          >
            Save
          </MDButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
